import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatabase, type Task } from '@/contexts/DatabaseContext';
import { useToast } from '@/contexts/ToastContext';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { exportTasksToXLSX, exportTasksToPDF } from '@/utils/export';

type FilterType = 'all' | 'mine' | 'todo' | 'inprogress' | 'done';
type SortType = 'date' | 'priority' | 'status';

export default function TasksPage() {
  const { t } = useTranslation('common');
  const { showToast } = useToast();
  const { tasks, trucks, users, currentUser, addTask, updateTask, deleteTask, addNotification } = useDatabase();
  const [filter, setFilter] = useState<FilterType>('all');
  const [sortBy, setSortBy] = useState<SortType>('date');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setExportOpen(false);
      }
    }
    if (exportOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportOpen]);

  // New task form
  const [newTask, setNewTask] = useState({
    truckId: '',
    title: '',
    description: '',
    assignedTo: '',
    priority: 'Moyenne' as 'Basse' | 'Moyenne' | 'Haute',
    dueDate: '',
  });

  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    switch (filter) {
      case 'mine':
        result = result.filter((task) => task.assignedTo === currentUser.id);
        break;
      case 'todo':
        result = result.filter((task) => task.status === 'À faire');
        break;
      case 'inprogress':
        result = result.filter((task) => task.status === 'En cours');
        break;
      case 'done':
        result = result.filter((task) => task.status === 'Terminé');
        break;
      default:
        break;
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { Haute: 3, Moyenne: 2, Basse: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case 'status':
          const statusOrder = { 'À faire': 1, 'En cours': 2, 'Terminé': 3 };
          return statusOrder[a.status] - statusOrder[b.status];
        case 'date':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

    return result;
  }, [tasks, filter, sortBy, currentUser.id]);

  const handleCreateTask = () => {
    if (!newTask.title.trim() || !newTask.truckId) return;
    const truck = trucks.find((t) => t.id === newTask.truckId);
    const assignee = users.find((u) => u.id === newTask.assignedTo);
    const task: Task = {
      id: String(Date.now()),
      truckId: newTask.truckId,
      truckDoorNumber: truck?.doorNumber || '',
      title: newTask.title,
      description: newTask.description,
      assignedTo: newTask.assignedTo || currentUser.id,
      assignedToName: assignee?.fullName || currentUser.fullName,
      status: 'À faire',
      priority: newTask.priority,
      dueDate: newTask.dueDate,
      createdAt: new Date().toISOString(),
    };
    addTask(task);

    // Notification
    addNotification({
      title: t('notifTaskCreated'),
      message: `${t('notifNewTask')} : ${task.title} (${task.truckDoorNumber})`,
      type: 'task_created',
      taskId: task.id,
      isRead: false,
    });

    setIsModalOpen(false);
    setNewTask({
      truckId: '',
      title: '',
      description: '',
      assignedTo: '',
      priority: 'Moyenne',
      dueDate: '',
    });
  };

  const handleStatusChange = (taskId: string, newStatus: 'À faire' | 'En cours' | 'Terminé') => {
    const task = tasks.find((t) => t.id === taskId);
    updateTask(taskId, { status: newStatus });

    // Notification
    if (task) {
      const statusLabel =
        newStatus === 'À faire'
          ? t('todo')
          : newStatus === 'En cours'
          ? t('inProgress')
          : t('done');
      addNotification({
        title: t('notifStatusChanged'),
        message: `${t('notifTaskStatusChanged')} « ${task.title} » ${t('notifTo')} ${statusLabel}`,
        type: 'task_status',
        taskId,
        isRead: false,
      });
    }
  };

  const handleDeleteTask = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    deleteTask(taskId);

    // Notification
    if (task) {
      addNotification({
        title: t('notifTaskDeleted'),
        message: `${t('notifTaskDeletedMsg')} : ${task.title} (${task.truckDoorNumber})`,
        type: 'task_deleted',
        taskId,
        isRead: false,
      });
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'Haute':
        return 'bg-accent-100 text-accent-800';
      case 'Moyenne':
        return 'bg-secondary-100 text-secondary-800';
      case 'Basse':
        return 'bg-primary-100 text-primary-800';
      default:
        return 'bg-background-100 text-foreground-600';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Terminé':
        return 'bg-primary-100 text-primary-800';
      case 'En cours':
        return 'bg-secondary-100 text-secondary-800';
      case 'À faire':
        return 'bg-accent-100 text-accent-800';
      default:
        return 'bg-background-100 text-foreground-600';
    }
  };

  const filterOptions = [
    { key: 'all' as FilterType, label: t('allTasks') },
    { key: 'mine' as FilterType, label: t('myTasks') },
    { key: 'todo' as FilterType, label: t('todo') },
    { key: 'inprogress' as FilterType, label: t('inProgress') },
    { key: 'done' as FilterType, label: t('done') },
  ];

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
            {t('tasks')}
          </h1>
          <div className="flex gap-2">
            {/* Export dropdown */}
            <div className="relative" ref={exportRef}>
              <button
                onClick={() => setExportOpen(!exportOpen)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-download-2-line" />
                {t('export')}
                <i className="ri-arrow-down-s-line text-xs" />
              </button>

              {exportOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-background-50 rounded-lg border border-background-200 shadow-lg z-50 py-1">
                  <button
                    onClick={() => {
                      exportTasksToXLSX(tasks, t);
                      showToast('Export Excel des tâches réussi.', 'success');
                      setExportOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 text-left"
                  >
                    <i className="ri-file-excel-2-line mr-2 text-primary-600" />
                    {t('exportExcel')}
                  </button>
                  <button
                    onClick={() => {
                      exportTasksToPDF(tasks, t);
                      showToast('Export PDF réussi.', 'success');
                      setExportOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 text-left"
                  >
                    <i className="ri-file-pdf-2-line mr-2 text-accent-600" />
                    {t('exportPDF')}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-add-line" />
              {t('addTask')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 mb-4 md:mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex flex-wrap gap-2">
              {filterOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setFilter(opt.key)}
                  className={`px-3 py-2 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                    filter === opt.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="sm:ml-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortType)}
                className="px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="date">{t('sortBy')} — {t('date')}</option>
                <option value="priority">{t('sortBy')} — {t('priority')}</option>
                <option value="status">{t('sortBy')} — {t('status')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Task Cards */}
        <div className="space-y-3">
          {filteredTasks.length === 0 ? (
            <div className="bg-background-50 rounded-xl border border-background-200 py-12 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-task-line text-foreground-400 text-xl" />
              </div>
              <p className="text-sm text-foreground-500">{t('noTasks')}</p>
            </div>
          ) : (
            filteredTasks.map((task) => (
              <div
                key={task.id}
                className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-5 hover:border-background-300 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="text-base font-semibold text-foreground-900">{task.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadge(task.status)}`}>
                        {task.status === 'À faire'
                          ? t('todo')
                          : task.status === 'En cours'
                          ? t('inProgress')
                          : t('done')}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-600 mb-3">{task.description}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-truck-line" />
                        {task.truckDoorNumber}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line" />
                        {task.assignedToName}
                      </span>
                      {task.dueDate && (
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {new Date(task.dueDate).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {task.status !== 'Terminé' && (
                      <button
                        onClick={() =>
                          handleStatusChange(
                            task.id,
                            task.status === 'À faire' ? 'En cours' : 'Terminé'
                          )
                        }
                        className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-xs font-medium hover:bg-primary-100 transition-colors whitespace-nowrap"
                      >
                        {task.status === 'À faire' ? t('inProgress') : t('done')}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteTask(task.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create Task Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => setIsModalOpen(false)}
            />
            <div className="relative bg-background-50 rounded-xl border border-background-200 shadow-xl w-full max-w-lg p-5 md:p-6">
              <h2 className="text-lg font-bold text-foreground-950 font-heading mb-5">
                {t('addTask')}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                    {t('trucks')}
                  </label>
                  <select
                    value={newTask.truckId}
                    onChange={(e) => setNewTask({ ...newTask, truckId: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="">{t('selectTruck')}</option>
                    {trucks.map((truck) => (
                      <option key={truck.id} value={truck.id}>
                        {truck.doorNumber} — {truck.licensePlate}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                    {t('taskTitle')}
                  </label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                    {t('taskDescription')}
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                      {t('assignedTo')}
                    </label>
                    <select
                      value={newTask.assignedTo}
                      onChange={(e) => setNewTask({ ...newTask, assignedTo: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      <option value="">{t('select')}</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.fullName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                      {t('priority')}
                    </label>
                    <select
                      value={newTask.priority}
                      onChange={(e) =>
                        setNewTask({ ...newTask, priority: e.target.value as 'Basse' | 'Moyenne' | 'Haute' })
                      }
                      className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      <option value="Basse">{t('low')}</option>
                      <option value="Moyenne">{t('medium')}</option>
                      <option value="Haute">{t('high')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                    {t('dueDate')}
                  </label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2.5 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleCreateTask}
                  disabled={!newTask.title.trim() || !newTask.truckId}
                  className="flex-1 px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}